/**
 * Word marks for Logs › Deviations. Edit Rate compares the distinct words of two texts
 * (lower-cased, split on whitespace — backend `wordOverlap`), so a word is marked exactly when
 * the other text does not use it. Whitespace is kept as its own segment so the text renders as written.
 */
export interface WordSegment {
	text: string;
	/** True for a word the other text does not use. */
	changed: boolean;
}

const distinctWords = (text: string): Set<string> => new Set(text.toLowerCase().split(/\s+/).filter(Boolean));

export function markWordChanges(text: string, otherText: string): WordSegment[] {
	const otherWords = distinctWords(otherText);
	return text
		.split(/(\s+)/)
		.filter(Boolean)
		.map((part) => ({ text: part, changed: part.trim() !== '' && !otherWords.has(part.toLowerCase()) }));
}

/**
 * Sentence marks for the Sentences view: a sentence is kept when the other text has it word for word,
 * reworded when one there shares enough of its words, else removed (from the draft) or added (to the
 * reply). Each sentence keeps the whitespace after it, so the text renders as written.
 */
export type SentenceChange = 'kept' | 'reworded' | 'removed' | 'added';

export interface SentenceSegment {
	text: string;
	change: SentenceChange;
}

/** Share of words a sentence must have in common with one in the other text to count as reworded. */
const REWORDED_OVERLAP = 0.35;

const SENTENCE = /[^.!?\n]+(?:[.!?]+|$)|\n+/g;

const sentencesOf = (text: string): string[] => text.match(SENTENCE) ?? [];

const looseWords = (text: string): Set<string> =>
	new Set(
		text
			.toLowerCase()
			.replace(/[^\p{L}\p{N}'{}]+/gu, ' ')
			.split(/\s+/)
			.filter(Boolean)
	);

const overlap = (a: string, b: string): number => {
	const wordsA = looseWords(a);
	const wordsB = looseWords(b);
	const shared = [...wordsA].filter((word) => wordsB.has(word)).length;
	const total = wordsA.size + wordsB.size - shared;
	return total === 0 ? 1 : shared / total;
};

export function markSentenceChanges(text: string, otherText: string, missing: 'removed' | 'added'): SentenceSegment[] {
	const others = sentencesOf(otherText)
		.map((sentence) => sentence.trim())
		.filter(Boolean);
	return sentencesOf(text).map((sentence) => {
		const trimmed = sentence.trim();
		if (!trimmed || others.includes(trimmed)) return { text: sentence, change: 'kept' };
		const closest = Math.max(0, ...others.map((other) => overlap(trimmed, other)));
		return { text: sentence, change: closest >= REWORDED_OVERLAP ? 'reworded' : missing };
	});
}
